import { type ReactNode } from 'react';
import {
	Popover as ChakraPopover,
	PopoverTrigger,
	PopoverContent,
	PopoverBody,
	PopoverArrow,
	Portal,
	type PopoverContentProps,
	type PopoverProps as ChakraPopoverProps,
} from '@chakra-ui/react';
import { checkboxMenuPopoverContentSx } from './CheckboxMenu';

/**
 * Popover primitive — `trigger` + `children` contract, portalled and
 * `strategy="fixed"` by default so it escapes overflow/clipping contexts and
 * stays in the viewport regardless of where the trigger sits.
 *
 * ENG-2279 / ENG-2145: hairline border, 10px radius, no drop shadow.
 */
export interface PopoverProps extends Omit<ChakraPopoverProps, 'children' | 'trigger'> {
	trigger: ReactNode;
	children: ReactNode;
	showArrow?: boolean;
	usePortal?: boolean;
	width?: PopoverContentProps['w'];
	contentProps?: Omit<PopoverContentProps, 'children' | 'sx'> & {
		[dataAttr: `data-${string}`]: string | boolean | undefined;
	};
}

export const Popover = ({
	trigger,
	children,
	showArrow = false,
	usePortal = true,
	gutter = 8,
	width,
	contentProps,
	strategy = 'fixed',
	...rest
}: PopoverProps) => {
	const content = (
		<PopoverContent w={width} sx={checkboxMenuPopoverContentSx} {...contentProps}>
			{showArrow ? <PopoverArrow /> : null}
			<PopoverBody p={0}>{children}</PopoverBody>
		</PopoverContent>
	);
	return (
		<ChakraPopover gutter={gutter} strategy={strategy} {...rest}>
			<PopoverTrigger>{trigger as any}</PopoverTrigger>
			{usePortal ? <Portal>{content}</Portal> : content}
		</ChakraPopover>
	);
};

export default Popover;
